import { useEffect, useState } from 'react';
import { useEventStore } from '../../../stores/eventStore';
import type { Event, EventAssignment } from '../../../types/event';
import EventSetlistDialog from './EventSetlistDialog';

const CATEGORY_COLORS: Record<string, string> = {
  String: 'bg-[#C8A96E]/20 text-[#C8A96E]',
  Wind: 'bg-blue-500/20 text-blue-300',
  Percussion: 'bg-purple-500/20 text-purple-300',
  Keys: 'bg-green-500/20 text-green-300',
  Vocal: 'bg-pink-500/20 text-pink-300',
};

const STATUS_ICON: Record<string, string> = {
  CONFIRMED: '✓',
  DECLINED: '×',
  ASSIGNED: '·',
};

const STATUS_LABEL: Record<string, string> = {
  CONFIRMED: 'Confirmado',
  DECLINED: 'Recusado',
  ASSIGNED: 'Pendente',
};

interface Props {
  teamId: string;
  events: Event[];
  canWriteSetlist: boolean;
}

export default function ScheduleMap({ teamId, events, canWriteSetlist }: Props) {
  const { assignments, loadAssignments } = useEventStore();
  const [loading, setLoading] = useState(false);
  const [filterMember, setFilterMember] = useState('');
  const [filterInstrument, setFilterInstrument] = useState('');
  const [filterEventType, setFilterEventType] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);

  const scheduledEvents = events.filter((e) => e.status === 'SCHEDULED').slice(0, 12);

  useEffect(() => {
    if (!scheduledEvents.length) return;
    setLoading(true);
    Promise.all(scheduledEvents.map((e) => loadAssignments(teamId, e.id))).finally(() =>
      setLoading(false)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, events.length, loadAssignments]);

  // Collect all unique members across all assignments
  const memberMap = new Map<string, { name: string; avatar: string }>();
  scheduledEvents.forEach((event) => {
    (assignments[event.id] ?? []).forEach((a) => {
      if (!memberMap.has(a.member_id)) {
        memberMap.set(a.member_id, { name: a.user_name, avatar: a.avatar_url });
      }
    });
  });
  const memberIds = Array.from(memberMap.keys());

  // Filter derivations
  const uniqueInstruments = Array.from(
    new Set(
      scheduledEvents.flatMap((e) => (assignments[e.id] ?? []).map((a) => a.instrument_name))
    )
  ).sort();

  const uniqueEventTypes = Array.from(
    new Set(scheduledEvents.map((e) => e.event_type_name).filter(Boolean) as string[])
  ).sort();

  const filteredMemberIds = memberIds.filter((id) => {
    if (filterMember && id !== filterMember) return false;
    if (filterInstrument) {
      const hasInstrument = scheduledEvents.some((e) =>
        (assignments[e.id] ?? []).some(
          (a) => a.member_id === id && a.instrument_name === filterInstrument
        )
      );
      if (!hasInstrument) return false;
    }
    return true;
  });

  const filteredEvents = filterEventType
    ? scheduledEvents.filter((e) => e.event_type_name === filterEventType)
    : scheduledEvents;

  const getAssignment = (memberId: string, eventId: string): EventAssignment | undefined =>
    (assignments[eventId] ?? []).find((a) => a.member_id === memberId);

  if (loading) {
    return <div className="py-12 text-center text-white/30 text-sm">Carregando escala...</div>;
  }

  if (scheduledEvents.length === 0) {
    return (
      <div className="py-12 text-center">
        <p className="text-white/40 text-sm">Nenhum evento agendado</p>
      </div>
    );
  }

  const selectCls = 'bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white/60 text-xs outline-none focus:border-[#C8A96E]/50 cursor-pointer';

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <select value={filterMember} onChange={(e) => setFilterMember(e.target.value)} className={selectCls}>
          <option value="">Todos os membros</option>
          {Array.from(memberMap.entries()).map(([id, m]) => (
            <option key={id} value={id}>{m.name}</option>
          ))}
        </select>
        <select value={filterInstrument} onChange={(e) => setFilterInstrument(e.target.value)} className={selectCls}>
          <option value="">Todos os instrumentos</option>
          {uniqueInstruments.map((inst) => (
            <option key={inst} value={inst}>{inst}</option>
          ))}
        </select>
        {uniqueEventTypes.length > 0 && (
          <select value={filterEventType} onChange={(e) => setFilterEventType(e.target.value)} className={selectCls}>
            <option value="">Todos os tipos</option>
            {uniqueEventTypes.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        )}
      </div>
      {memberIds.length === 0 && (
        <p className="mb-4 hidden text-center text-sm text-white/40 md:block">Nenhuma atribuição nos próximos eventos</p>
      )}
      <div className="space-y-3 md:hidden">
        {filteredEvents.map((event) => {
          const eventAssignments = (assignments[event.id] ?? []).filter((assignment) => {
            if (filterMember && assignment.member_id !== filterMember) return false;
            if (filterInstrument && assignment.instrument_name !== filterInstrument) return false;
            return true;
          });

          return (
            <article
              key={event.id}
              aria-label={`Evento ${event.name}`}
              className="rounded-lg border border-white/10 bg-white/[0.03] p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-white">{event.name}</p>
                  <p className="mt-1 text-xs text-white/40">
                    {new Date(`${event.date}T12:00:00`).toLocaleDateString('pt-BR', {
                      weekday: 'short',
                      day: '2-digit',
                      month: 'short',
                    })}
                    {event.start_time && ` · ${event.start_time.slice(0, 5)}`}
                  </p>
                </div>
                {event.setlist_id && (
                  <button
                    type="button"
                    aria-label={`Ver repertório de ${event.name}`}
                    onClick={() => setSelectedEvent(event)}
                    className="shrink-0 text-xs text-[#C8A96E]"
                  >
                    Repertório
                  </button>
                )}
              </div>
              <div className="mt-3 border-t border-white/5 pt-3">
                {eventAssignments.length === 0 ? (
                  <p className="text-xs text-white/35">Nenhum membro atribuído</p>
                ) : (
                  <ul className="space-y-2">
                    {eventAssignments.map((assignment) => (
                      <li key={assignment.id} className="flex items-center justify-between gap-3 text-xs">
                        <span className="min-w-0 truncate text-white/80">{assignment.user_name}</span>
                        <span className="shrink-0 text-white/50">
                          {assignment.instrument_name} · {STATUS_LABEL[assignment.status]}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </article>
          );
        })}
      </div>
      <div className="hidden overflow-x-auto md:block">
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th className="text-left text-xs text-white/40 font-normal pb-3 pr-4 min-w-[140px]">Membro</th>
            {filteredEvents.map((event) => (
              <th key={event.id} className="text-center text-xs text-white/40 font-normal pb-3 px-2 min-w-[80px]">
                <div>{new Date(event.date + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}</div>
                {event.setlist_id ? (
                  <button
                    type="button"
                    aria-label={`Ver repertório de ${event.name}`}
                    onClick={() => setSelectedEvent(event)}
                    className="max-w-[80px] truncate font-normal text-[#C8A96E]/70 hover:text-[#C8A96E]"
                  >
                    {event.name}
                  </button>
                ) : (
                  <div className="text-white/20 font-normal truncate max-w-[80px]">{event.name}</div>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filteredMemberIds.map((memberId) => {
            const member = memberMap.get(memberId)!;
            return (
              <tr key={memberId} className="border-t border-white/5">
                <td className="py-3 pr-4">
                  <p className="text-white text-xs font-medium">{member.name}</p>
                </td>
                {filteredEvents.map((event) => {
                  const a = getAssignment(memberId, event.id);
                  return (
                    <td key={event.id} className="text-center py-3 px-2">
                      {a ? (
                        <div className="flex flex-col items-center gap-1">
                          <span
                            className={`px-2 py-0.5 rounded text-xs ${
                              CATEGORY_COLORS[a.instrument_category] ?? 'bg-white/10 text-white/60'
                            }`}
                          >
                            {a.instrument_name}
                          </span>
                          <span className="text-xs text-white/30">{STATUS_ICON[a.status]}</span>
                        </div>
                      ) : (
                        <span className="text-white/10">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      {selectedEvent && (
        <EventSetlistDialog
          teamId={teamId}
          event={selectedEvent}
          canWrite={canWriteSetlist}
          onClose={() => setSelectedEvent(null)}
        />
      )}
    </div>
  );
}
